// Everything that happened to one action, newest first, grouped by day: created, changed,
// dates proposed, kept and moved, sends and replies, resolved. Fetched for the whole
// specialist page the first time any row's History opens.
// See docs/Y2026-Q4/design-action-dates-dependencies-and-ranking.md 4.3.
import { useState } from "react";
import { Link } from "react-router-dom";

import {
  type HistoryEntry,
  useActionHistory,
} from "../api/hooks/useActionChanges";
import { runLinkPath } from "../routes/learningAgentPaths";
import { formatDay, isoDay } from "../utils/attributeFormat";
import { closedByLabel, namePeople } from "../utils/nextActions";
import { agentDisplayLabel } from "../utils/runParticipants";
import styles from "./NextActionTable.module.css";
import own from "./ActionHistory.module.css";
import { WrittenBeforeBranch } from "./AgentSlotBar";

const byDay = (entries: HistoryEntry[]) => {
  const days: { day: string; entries: HistoryEntry[] }[] = [];
  for (const entry of entries) {
    const day = isoDay(new Date(entry.at));
    const last = days[days.length - 1];
    if (last?.day === day) last.entries.push(entry);
    else days.push({ day, entries: [entry] });
  }
  return days;
};

export const ActionHistory = ({
  projectId,
  agent,
  itemId,
  team,
  moves,
}: {
  projectId: string;
  agent: string;
  itemId: string;
  team: Record<string, string>;
  /** The first due date and how many times it moved, which heads the list. */
  moves: { first: string; count: number } | null;
}) => {
  const [open, setOpen] = useState(false);
  const history = useActionHistory(projectId, agent, open);
  const entries = history.data?.items[itemId] ?? [];
  // This action's own agent goes unnamed: only someone else's change says who made it.
  const who = (by: string): string | null => {
    if (by === `agent:${agent}`) return null;
    if (by === "platform") return "The platform";
    const review = /^agent:([a-z_]+) \(review by (.+)\)$/.exec(by);
    if (review?.[1] === agent)
      return `applying ${team[review[2]] ?? review[2]}'s review`;
    return closedByLabel(by, team, agentDisplayLabel);
  };
  return (
    <details
      className={styles.history}
      onToggle={(e) => setOpen(e.currentTarget.open)}
    >
      <summary>History</summary>
      {open && (
        <div className={own.history}>
          {moves && (
            <p className={styles.small}>
              Originally due {formatDay(moves.first)}, moved {moves.count} time
              {moves.count === 1 ? "" : "s"}.
            </p>
          )}
          {history.isLoading && <p className={styles.small}>Loading…</p>}
          {history.isError && (
            <p className={styles.small}>Could not load this action's history.</p>
          )}
          {history.isSuccess && entries.length === 0 && (
            <p className={styles.small}>Nothing recorded yet.</p>
          )}
          {byDay(entries).map(({ day, entries: onDay }) => (
            <section key={day} className={own.day}>
              <h6 className={own.dayLabel}>{formatDay(day)}</h6>
              {onDay.map((entry, index) => {
                const by = who(entry.by);
                return (
                  <div key={`${entry.at}-${index}`} className={own.entry}>
                    <span className={own.time}>
                      {new Date(entry.at).toLocaleTimeString("en-GB", {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>
                    <span className={own.why}>
                      <strong className={own.what}>
                        {namePeople(entry.what, team)}
                        {by && <span className={own.who}> · {by}</span>}
                        {entry.reason && ":"}
                      </strong>
                      {entry.reason && ` ${namePeople(entry.reason, team)}`}
                      {entry.run_id && (
                        <>
                          {" · "}
                          <Link className={own.run} to={runLinkPath(entry.run_id)}>
                            Run
                          </Link>
                          <WrittenBeforeBranch at={entry.at} />
                        </>
                      )}
                    </span>
                  </div>
                );
              })}
            </section>
          ))}
        </div>
      )}
    </details>
  );
};
